import { useNavigate, useParams } from "react-router-dom";
import "./Ticket.scss";
import { useEffect, useRef, useState } from "react";
import { FiSearch } from "react-icons/fi";
import {
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
} from "@mui/material";
import { showToast } from "../../../toastService.js";
import { motion } from "framer-motion";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { customSelectStyles_dark, customSelectStyles_light } from "../../../components/ui/CustomSelect.jsx";
import { useSelector } from "react-redux";
import newRequest from "../../../utils/newRequest.js";
import { ScaleLoader } from "react-spinners";
import CircularButton, { CustomLoadingButton } from "../../../components/ui/CustomButton.jsx";
import ConfirmDialog from "../../../components/confirmDialog/confirmDialog.jsx";
import { MdClose, MdSave, MdPersonAddAlt1, MdForum } from "react-icons/md";
import { useTranslation } from "react-i18next";
import OnScreenReports from "../../../components/onScreenReports/OnScreenReports.jsx";
import { IoDocumentTextSharp } from "react-icons/io5";
import { logData } from "../../../utils/genericFunctions.js";
import { useMemo } from "react";
import InitialsAvatar from "../../../components/InitialsAvatar/InitialsAvatar.jsx";
import ExpandableComponent from "../../../components/expandableComponent/ExpandableComponent.jsx";
import { RiAttachment2 } from "react-icons/ri";
import FileUploadComponent from "../../../components/fileUploadComponent/FileUploadComponent.jsx";
import AllFiles from "../../../components/allFiles/AllFiles.jsx";
import { LucideListCollapse } from "lucide-react";
import { IoMdInformationCircle } from "react-icons/io";
import Collaborator from "../../../components/Collaborator/Collaborator.jsx";
import AllDocsNote from "../../../components/allDocsNote/AllDocsNote.jsx";


// --- small helpers ---
const initials = (name = "") =>
    name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() || "")
        .join("");

const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};


const splitDuration = (totalSeconds = 0) => {
    const s = Math.max(0, Math.floor(totalSeconds));
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return { d, h, m, s: sec };
};


const Ticket = () => {
    const { t } = useTranslation();
    const { docid } = useParams();
    const navigate = useNavigate();
    const { currentTheme } = useSelector((state) => state.theme);
    const { currentUser } = useSelector((state) => state.user);

    const [isCollabOpen, setIsCollabOpen] = useState(false);
    const openCollab = () => setIsCollabOpen(true);
    const closeCollab = () => setIsCollabOpen(false);
    const pageShellRef = useRef(null);
    const [collabRightOffset, setCollabRightOffset] = useState(0);

    const [isLoading, setIsLoading] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [confirmDialogAction, setConfirmDialogAction] = useState("None");
    const [confirmationActiontext, setConfirmationActiontext] = useState("");
    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyDialogTitle, setNotifyDialogTitle] = useState("Confirm");
    const [notifyDialogText, setNotifyDialogText] = useState("Text");
    const [searchTerm, setSearchTerm] = useState("");
    const [reportsDialogOpen, setReportsDialogOpen] = useState(false);
    const [documentEnabled, setDocumentEnabled] = useState("Y");

    const [nextAction, setNextAction] = useState("Validate");
    const [ticketData, setTicketData] = useState(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [newComment, setNewComment] = useState("");
    const [commentVisibility, setCommentVisibility] = useState("Public"); // Public | Internal

    const [attachmentsData, setAttachmentsData] = useState([]);

    const priorityOptions = useMemo(
        () => [
            { value: "Low", label: "Low" },
            { value: "Normal", label: "Normal" },
            { value: "High", label: "High" },
            { value: "Urgent", label: "Urgent" },
            { value: "Critical", label: "Critical" },
        ],
        []
    );

    const canEditPriority = useMemo(() => {
        const ownerId = ticketData?.tk_owner?._id || ticketData?.tk_owner;
        const userId = currentUser?._id || currentUser?.usr_id || currentUser?.id;
        return ownerId && userId && String(ownerId) === String(userId);
    }, [ticketData, currentUser]);

    const priorityValue = useMemo(() => {
        const p = ticketData?.tk_priority || "Normal";
        return priorityOptions.find((x) => x.value === p) || priorityOptions[1];
    }, [ticketData, priorityOptions]);

    const watchers = useMemo(() => {
        const raw = ticketData?.tk_watchers || [];
        // populated shape: { user_id: {_id, usr_full_name, usr_photo_url} }
        return raw
            .map((w) => w?.user_id)
            .filter(Boolean)
            .map((u) => ({
                id: u?._id,
                name: u?.usr_full_name || "",
                photo: u?.usr_photo_url || "",
            }));
    }, [ticketData]);

    const comments = useMemo(() => {
        const raw = ticketData?.tk_comments || [];
        // assume populated: cm_created_by => {_id, usr_full_name, usr_photo_url}
        return raw
            .filter((c) => c?.is_deleted !== "Y")
            .map((c) => ({
                id: c?._id,
                body: c?.cm_body || "",
                type: c?.cm_type || "Comment",
                visibility: c?.cm_visibility || "Public",
                at: c?.cm_created_at,
                user: c?.cm_created_by
                    ? {
                        id: c.cm_created_by?._id,
                        name: c.cm_created_by?.usr_full_name || "",
                        photo: c.cm_created_by?.usr_photo_url || "",
                    }
                    : { id: null, name: "System", photo: "" },
            }));
    }, [ticketData]);

    // SLA card calculations
    const slaUi = useMemo(() => {
        const sla = ticketData?.tk_sla || {};
        const enabled = String(sla?.sla_enabled || "N") === "Y";

        const targetMins = Number(sla?.resolution_target_mins || 0);
        const startedAt = sla?.started_at || ticketData?.createdAt;

        return {
            enabled,
            targetMins,
            startedAt,
            targetSecs: Math.max(0, Math.round(targetMins * 60)),
            breached: String(sla?.resolution_breached || "N") === "Y",
            approaching: false, // computed below in component with remainingSecs
        };
    }, [ticketData]);
    // add these states + effect inside Ticket component (below slaUi)
    const [slaNowTick, setSlaNowTick] = useState(Date.now()); // drives 1-second UI updates

    useEffect(() => {
        // only tick when SLA is enabled AND not breached
        if (!slaUi.enabled || slaUi.breached) return;

        const id = setInterval(() => setSlaNowTick(Date.now()), 1000);
        return () => clearInterval(id);
    }, [slaUi.enabled, slaUi.breached]);

    const slaComputed = useMemo(() => {
        const started = slaUi.startedAt ? new Date(slaUi.startedAt).getTime() : null;
        const now = slaNowTick;

        const elapsedSecs = started ? Math.max(0, Math.floor((now - started) / 1000)) : 0;
        const remainingSecs = slaUi.targetSecs > 0 ? slaUi.targetSecs - elapsedSecs : 0;

        const breached = slaUi.targetSecs > 0 ? remainingSecs <= 0 : false;

        // "approaching" = last 25% of target (only while not breached)
        const approaching =
            slaUi.targetSecs > 0
                ? remainingSecs > 0 && remainingSecs <= Math.ceil(slaUi.targetSecs * 0.25)
                : false;

        // display rules:
        // - before breach: show D:H:M:S (include days if >= 1 day)
        // - after breach: show D:H:M only (include days if >= 1 day)
        const remainingAbsSecs = Math.abs(remainingSecs);
        const parts = splitDuration(remainingAbsSecs);

        return {
            elapsedSecs,
            remainingSecs,
            breached: slaUi.breached || breached,
            approaching,
            parts, // {d,h,m,s}
        };
    }, [slaUi.targetSecs, slaUi.startedAt, slaUi.breached, slaNowTick]);


    const fetchTicket = async (firstTime = false) => {
        try {
            if (firstTime) {
                setIsLoading(true);
            }
            const res = await newRequest.get(`/ticket/find/${docid}`);
            // logData("ticket", res.data);
            setTicketData(res.data);
            // fetch attachments
            fetchDocumentAttachments("TK Ticket", res.data._id);
        } catch (err) {
            console.log(err);
        } finally {
            if (firstTime) {
                setIsLoading(false);
            }
        }
    };

    const fetchDocumentAttachments = async (docType, docid) => {
        try {
            const res = await newRequest.post(
                "/files/findDocFiles",
                {
                    fl_doc_type: docType,
                    fl_parent_doc_id: docid,
                },

            );

            setAttachmentsData(res.data);
        } catch (err) {
            console.log(err);
        }
    };

    const handleConfirm = async () => {
        try {

        } catch (err) {
            console.log(err);
        }
        setIsConfirmDialogOpen(false);
    };

    const saveRecord = async () => {
        try {
            if (!ticketData?._id) return;
            setIsSaving(true);

            const payload = {
                tk_priority: ticketData?.tk_priority,
                // add more editable fields here later
            };

            const res = await newRequest.put(`/ticket/upd/${ticketData._id}`, payload);
            if (res?.data?.msg === "updated") {
                await fetchTicket(false);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setIsSaving(false);
        }
    };

    const postComment = async () => {
        try {
            const body = String(newComment || "").trim();
            if (!body) return;

            setIsSaving(true);
            const res = await newRequest.post(`/ticket/addComment/${ticketData._id}`, {
                cm_body: body,
                cm_type: commentVisibility === "Internal" ? "InternalNote" : "Comment",
                cm_visibility: commentVisibility,
            });

            if (res?.data?.msg === "added") {
                setNewComment("");
                await fetchTicket(false);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        const compute = () => {
            const el = pageShellRef.current;
            if (!el) return;

            const rect = el.getBoundingClientRect();
            // how much “empty space” exists on the right side of the centered/max-width container
            const offset = Math.max(0, window.innerWidth - rect.right);
            setCollabRightOffset(offset);
        };

        compute();

        window.addEventListener("resize", compute);
        // optional: if your layout can shift on scroll due to sticky headers etc.
        window.addEventListener("scroll", compute, true);

        return () => {
            window.removeEventListener("resize", compute);
            window.removeEventListener("scroll", compute, true);
        };
    }, []);

    useEffect(() => {
        fetchTicket(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
            color="var(--primary)"
            height={50}
            radius={8}
            width={4}
          />
                </div>
            ) : (
                <motion.div
                    className="ticket-category"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="ticket-navbar">
                        <div className="ticket-navbar-left">
                            <span className="page-title">{ticketData?.tk_no || t('ticket')}</span>
                            <div className="ticket-breadcrumbs">
                                <span className="ticket-breadcrumb">{ticketData?.tk_category || "—"}</span>
                                <span className="ticket-breadcrumb-sep">›</span>
                                <span className="ticket-breadcrumb">{ticketData?.tk_priority || "—"} {t('priority')}</span>
                                <span className="ticket-breadcrumb-sep">›</span>
                                <span className="ticket-breadcrumb">{ticketData?.tk_no || "—"}</span>
                            </div>
                        </div>
                        <div className="ticket-navbar-right">
                            {documentEnabled === "Y" && (
                                <CustomLoadingButton
                                    currentTheme={currentTheme}
                                    iconPosition="end"
                                    borderRadiusType="both"
                                    variant="tint"
                                    color="#00D37F"
                                    tooltip="Save changes"
                                    endIcon={<MdSave />}
                                    onClick={saveRecord}>
                                    {t('save')}
                                </CustomLoadingButton>
                            )}
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="both"
                                variant="tint"
                                color="#2563EB"
                                tooltip="Run reports for this document"
                                endIcon={<IoDocumentTextSharp />}
                                onClick={() => setReportsDialogOpen(true)}>
                                {t('reports')}
                            </CustomLoadingButton>
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="both"
                                variant="tint"
                                color="#f55656"
                                tooltip="Close this document"
                                endIcon={<MdClose />}
                                onClick={() => navigate(-1)}>
                                {t('close')}
                            </CustomLoadingButton>
                            <div className="collaborator-icon"
                                onClick={openCollab}>
                                <MdForum className="icon" />
                            </div>
                        </div>
                    </div>
                    <div className="ticket-category-content">
                        <div className="ticket-content-wrapper">
                            {/* LEFT */}
                            <div className="ticket-left-wrapper">
                                <div className="ticket-header">
                                    <div className="ticket-header-left">
                                        <h1 className="ticket-title">{ticketData?.tk_subject || "—"}</h1>
                                        <div className="ticket-meta-row">
                                            <div className="ticket-meta-user">
                                                <div className="ticket-avatar small">
                                                    {ticketData?.tk_owner?.usr_photo_url ? (
                                                        <img src={ticketData.tk_owner.usr_photo_url} alt="" className="ticket-avatar-img" />
                                                    ) : (
                                                        <span className="ticket-avatar-initials">
                                                            {/* {initials(ticketData?.tk_owner?.usr_full_name || "System")} */}
                                                            <InitialsAvatar
                                                                name={ticketData?.tk_owner?.usr_full_name || "System"}
                                                                size={32}
                                                                currentTheme={currentTheme}
                                                            />
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="ticket-meta-text">
                                                    {t('reportedBy')}{" "}
                                                    <b>{ticketData?.tk_owner?.usr_full_name || "System"}</b>
                                                </span>
                                            </div>
                                            <div className="ticket-meta-dot" />
                                            <span className="ticket-meta-text">
                                                {fmtDate(ticketData?.createdAt)}
                                            </span>
                                            <div className="ticket-pill priority">
                                                {t('priority').toUpperCase()}: {ticketData?.tk_priority || "Normal"}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Detailed Description */}
                                <ExpandableComponent
                                    title={<div className="ticket-card-block-title">
                                        <IoMdInformationCircle className="ticket-card-block-icon" />
                                        <span>{t('detailedDesc')}</span>
                                    </div>}
                                    subtitle={""}
                                    defaultExpanded={true}
                                >
                                    <div className="ticket-card-block-body">
                                        <p className="ticket-description">
                                            {ticketData?.tk_description || "—"}
                                        </p>

                                        {/* Optional "Reproduced Observations" feel */}
                                        {Array.isArray(ticketData?.tk_custom_fields) &&
                                            ticketData.tk_custom_fields.length > 0 && (
                                                <div className="ticket-observations">
                                                    <div className="ticket-observations-title">Reproduced Observations:</div>
                                                    <div className="ticket-observations-list">
                                                        {ticketData.tk_custom_fields.slice(0, 3).map((cf) => (
                                                            <div className="ticket-observation" key={cf?._id || cf?.cf_key}>
                                                                <span className="ticket-observation-dot" />
                                                                <span className="ticket-observation-text">
                                                                    {cf?.cf_label || cf?.cf_key}:{" "}
                                                                    <b>{String(cf?.cf_value ?? "")}</b>
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                    </div>
                                </ExpandableComponent>
                                <ExpandableComponent
                                    title={<div className="ticket-card-block-title">
                                        <RiAttachment2 className="ticket-card-block-icon" />
                                        <span>{t('attachments')}</span>
                                    </div>}
                                    subtitle={
                                        attachmentsData?.length > 1 ? `${attachmentsData.length} ${t('attachments')}`
                                            : attachmentsData?.length === 1 ? `${attachmentsData.length} ${t('attachment')}`
                                                : t('noAttachments')}
                                    defaultExpanded={attachmentsData?.length > 0 ? true : false}
                                >
                                    <div className="attachments">
                                        <FileUploadComponent
                                            documentNo={ticketData?.tk_no}
                                            documentId={ticketData?._id}
                                            documentType="TK Ticket"
                                            fetchDocumentAttachments={fetchDocumentAttachments}
                                            uploadFolder={`tk-ticket/${ticketData?._id}`}
                                        />
                                        <div className="attachment-files">
                                            <AllFiles filesList={attachmentsData} />
                                        </div>
                                    </div>
                                </ExpandableComponent>
                                {/* Activity & Collaboration */}
                                <ExpandableComponent
                                    title={<div className="ticket-card-block-title">
                                        <LucideListCollapse className="ticket-activity-icon" />
                                        <span>{t('activityAndCollab')}</span>
                                    </div>}
                                    subtitle={""}
                                    defaultExpanded={true}
                                >
                                    <AllDocsNote
                                        docId={ticketData?._id}
                                        docType="Ticket"
                                        noteType={"Normal"}
                                    />
                                </ExpandableComponent>

                            </div>

                            {/* RIGHT */}
                            <div className="ticket-right-wrapper">
                                {/* TIME TO RESOLVE */}
                                <div className="ticket-side-card">
                                    <div className="ticket-side-card-top">
                                        <div className="ticket-side-label">{t("timeToResolve").toUpperCase()}</div>

                                        <div className="ticket-side-time">
                                            {/* DAYS (show if > 24h remaining OR > 24h breached) */}
                                            {(slaComputed.parts.d > 0) && (
                                                <>
                                                    <motion.span
                                                        key={`d-${slaComputed.parts.d}`}
                                                        className="num"
                                                        initial={{ y: -6, opacity: 0.6 }}
                                                        animate={{ y: 0, opacity: 1 }}
                                                        transition={{ duration: 0.18 }}
                                                    >
                                                        {String(slaComputed.parts.d).padStart(2, "0")}
                                                    </motion.span>
                                                    <span className="unit">d</span>
                                                </>
                                            )}

                                            {/* HOURS */}
                                            <span className="num">{String(slaComputed.parts.h).padStart(2, "0")}</span>
                                            <span className="unit">h</span>

                                            {/* MINUTES */}
                                            <span className="num">{String(slaComputed.parts.m).padStart(2, "0")}</span>
                                            <span className="unit">m</span>

                                            {/* SECONDS (only before breach) */}
                                            {slaComputed.parts.d === 0 &&
                                                !slaComputed.breached && (
                                                    <>
                                                        <motion.span
                                                            key={`s-${slaComputed.parts.s}`} // re-animates every second
                                                            className="num secs"
                                                            initial={{ scale: 0.92, opacity: 0.65 }}
                                                            animate={{ scale: 1, opacity: 1 }}
                                                            transition={{ duration: 0.12 }}
                                                        >
                                                            {String(slaComputed.parts.s).padStart(2, "0")}
                                                        </motion.span>
                                                        <span className="unit">s</span>
                                                    </>
                                                )}
                                        </div>

                                        {(slaComputed.approaching || slaComputed.breached) && (
                                            <div className={`ticket-side-alert ${slaComputed.breached ? "breached" : "approaching"}`}>
                                                {slaComputed.breached ? t("breached").toUpperCase() : t("approachingBreach").toUpperCase()}
                                            </div>
                                        )}
                                    </div>

                                    <div className="ticket-side-divider" />

                                    <div className="ticket-side-grid">
                                        <div className="ticket-side-row">
                                            <div className="ticket-side-small-label">{t('slaCategory').toUpperCase()}</div>
                                            <div className="ticket-side-strong">
                                                {ticketData?.tk_priority === "Critical"
                                                    ? "Critical (P1)"
                                                    : ticketData?.tk_priority === "Urgent"
                                                        ? "Urgent (P2)"
                                                        : "Standard"}
                                            </div>
                                        </div>

                                        <div className="ticket-side-row split">
                                            <div>
                                                <div className="ticket-side-small-label">{t('targetToResolutionTime').toUpperCase()}</div>
                                                <div className="ticket-side-strong">
                                                    {slaUi.target > 0 ? `${Math.round(slaUi.target / 60)} Hours` : "—"}
                                                </div>
                                            </div>
                                            <div className="ticket-side-right-muted">
                                                {slaUi.startedAt ? `Started ${ticketData?.time_ago_xx || ""}` : ""}
                                            </div>
                                        </div>

                                        <div className="ticket-side-row split">
                                            <div>
                                                <div className="ticket-side-small-label first">{t('firstResponse').toUpperCase()}</div>
                                                <div className="ticket-side-pill met">Met</div>
                                            </div>
                                            <div>
                                                <div className="ticket-side-small-label">{t('resolution').toUpperCase()}</div>
                                                <div className="ticket-side-pill active">
                                                    {ticketData?.tk_status === "Resolved" || ticketData?.tk_status === "Closed"
                                                        ? "Met"
                                                        : "Active"}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* WATCHERS */}
                                <div className="ticket-side-card">
                                    <div className="ticket-side-card-header">
                                        <div className="ticket-side-title">{t('activeWatchers').toUpperCase()}</div>
                                        <div className="ticket-side-count">{watchers.length}</div>
                                    </div>

                                    <div className="ticket-watchers-row">
                                        <div className="ticket-watchers-avatars">
                                            {watchers.slice(0, 3).map((w) => (
                                                <div className="ticket-avatar tiny" key={w.id}>
                                                    {w.photo ? (
                                                        <img src={w.photo} alt="" className="ticket-avatar-img" />
                                                    ) : (
                                                        <span className="ticket-avatar-initials">{initials(w.name || "U")}</span>
                                                    )}
                                                </div>
                                            ))}
                                            {watchers.length > 3 && (
                                                <div className="ticket-avatar tiny more">
                                                    <span className="ticket-avatar-initials">+{watchers.length - 3}</span>
                                                </div>
                                            )}
                                        </div>

                                        <button type="button" className="ticket-icon-btn">
                                            <MdPersonAddAlt1 />
                                        </button>
                                    </div>

                                    <div className="ticket-watcher-search">
                                        <input
                                            className="ticket-watcher-input"
                                            placeholder="Search and add watchers..."
                                            type="text"
                                            disabled
                                        />
                                    </div>
                                </div>

                                {/* PROPERTIES */}
                                <div className="ticket-side-card">
                                    <div className="ticket-side-title">{t('properties').toUpperCase()}</div>

                                    <div className="ticket-prop">
                                        <div className="ticket-prop-label">{t('assignedTo').toUpperCase()}</div>
                                        <div className="ticket-prop-user">
                                            <div className="ticket-avatar tiny">
                                                {ticketData?.tk_assigned_to?.usr_photo_url ? (
                                                    <img src={ticketData.tk_assigned_to.usr_photo_url} alt="" className="ticket-avatar-img" />
                                                ) : (
                                                    <span className="ticket-avatar-initials">
                                                        {initials(ticketData?.tk_assigned_to?.usr_full_name || "—")}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="ticket-prop-user-text">
                                                <div className="ticket-prop-user-name">
                                                    {ticketData?.tk_assigned_to?.usr_full_name || "Unassigned"}
                                                </div>
                                                <div className="ticket-prop-user-sub"> </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="ticket-prop-grid">
                                        <div className="ticket-prop-item">
                                            <div className="ticket-prop-label">{t('type').toUpperCase()}</div>
                                            <div className="ticket-prop-value">{ticketData?.tk_type || "—"}</div>
                                        </div>
                                        <div className="ticket-prop-item">
                                            <div className="ticket-prop-label">{t('tags').toUpperCase()}</div>
                                            <div className="ticket-prop-tags">
                                                {(ticketData?.tk_tags || []).slice(0, 2).map((tg) => (
                                                    <span className="ticket-tag" key={tg}>
                                                        {tg}
                                                    </span>
                                                ))}
                                                {(ticketData?.tk_tags || []).length > 2 && (
                                                    <span className="ticket-tag muted">+{ticketData.tk_tags.length - 2}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Priority edit (owner only) */}
                                    <div className="ticket-prop">
                                        <div className="ticket-prop-label">PRIORITY</div>
                                        <div className="ticket-prop-priority">
                                            {canEditPriority ? (
                                                <div className="ticket-priority-select">
                                                    <Select
                                                        value={priorityValue}
                                                        options={priorityOptions}
                                                        onChange={(opt) => {
                                                            setTicketData((p) => ({ ...p, tk_priority: opt?.value }));
                                                            setIsDirty(true);
                                                        }}
                                                        styles={currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light}
                                                        isSearchable={false}
                                                    />
                                                </div>
                                            ) : (
                                                <span className={`ticket-pill priority small`}>
                                                    {ticketData?.tk_priority || "Normal"}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <button type="button" className="ticket-audit-btn">
                                        VIEW FULL AUDIT HISTORY
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Collaborator
                        open={isCollabOpen}
                        docType={"Ticket"}
                        docNo={ticketData?.tk_no}
                        docId={ticketData?._id}
                        currentTheme={currentTheme}
                        onClose={closeCollab}
                        rightOffset={collabRightOffset}
                        currentUser={currentUser}
                    />
                </motion.div>
            )}
            {reportsDialogOpen && (
                <motion.div
                    className="overlay-dialog reports"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <h1 className="main-title">{t('reports')}</h1>
                            <CircularButton currentTheme={currentTheme} onClick={() => setReportsDialogOpen(false)} icon={<MdClose />} width={"35px"} height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            <OnScreenReports
                                reportGroupArray={["tickets"]}
                                documentData={{
                                    docNo: ticketData?.tk_no,
                                    docType: "Ticket",
                                    docId: ticketData?._id,
                                    ticket_docid: ticketData?._id
                                }} />
                        </div>
                    </div>
                </motion.div>
            )}
            {/* Dialog to notify user about changes being saved */}
            <Dialog open={isNotifyDialogOpen}
                onClose={() => setIsNotifyDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: currentTheme === "dark" ? "#181A20" : "#fff",
                        color: currentTheme === "dark" ? "#fff" : "#000",
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                    },
                }}>
                <DialogTitle>{notifyDialogTitle}</DialogTitle>
                <DialogContent>
                    <div dangerouslySetInnerHTML={{ __html: notifyDialogText }} />
                </DialogContent>
                <DialogActions>
                    <CustomLoadingButton currentTheme={currentTheme} variant="tint" color="#E040FB" onClick={() => setIsNotifyDialogOpen(false)}>{t('close')}</CustomLoadingButton>
                </DialogActions>
            </Dialog>
            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                onClose={() => setIsConfirmDialogOpen(false)}
                onConfirm={handleConfirm}
                dialogTitle={t('confirmation')}
                dialogContent={<p dangerouslySetInnerHTML={{ __html: confirmationActiontext }} />}
            />
        </div>
    )
}

export default Ticket;